import React, { useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useContactInfo } from "@/hooks/useContactInfo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { useQueryClient } from "@tanstack/react-query";

interface ContactInfoFormData {
  type: string;
  label: string;
  value: string;
  is_primary: boolean;
  display_order: number;
  is_public: boolean;
}

const AdminContact: React.FC = () => {
  const { data: contactInfo = [] } = useContactInfo();
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [editingContact, setEditingContact] = useState<number | null>(null);
  const [contactForm, setContactForm] = useState<ContactInfoFormData>({
    type: "",
    label: "",
    value: "",
    is_primary: false,
    display_order: 0,
    is_public: false,
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from("contact_information")
        .insert([contactForm]);

      if (error) throw error;

      toast({ title: "Success", description: "Contact info added successfully" });
      setIsAddingContact(false);
      setContactForm({ type: "", label: "", value: "", is_primary: false, display_order: 0, is_public: false });
      queryClient.invalidateQueries({ queryKey: ["contact-info"] });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add contact info",
      });
    }
  };

  const handleUpdateContact = async (id: number) => {
    try {
      const { error } = await supabase
        .from("contact_information")
        .update(contactForm)
        .eq("id", id);

      if (error) throw error;

      toast({ title: "Success", description: "Contact info updated successfully" });
      setEditingContact(null);
      setContactForm({ type: "", label: "", value: "", is_primary: false, display_order: 0, is_public: false });
      queryClient.invalidateQueries({ queryKey: ["contact-info"] });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update contact info",
      });
    }
  };

  const handleDeleteContact = async (id: number) => {
    if (!confirm("Are you sure you want to delete this contact info?")) return;

    try {
      const { error } = await supabase
        .from("contact_information")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({ title: "Success", description: "Contact info deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["contact-info"] });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete contact info",
      });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-display font-bold text-lounge-gold">Contact Information</h1>
          <p className="text-lounge-light/60 mt-2">Manage your contact details</p>
        </div>

        <div className="bg-lounge-charcoal p-6 rounded-lg border border-lounge-gold/20">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-display font-semibold text-lounge-gold">Contact Details</h2>
            <Button
              onClick={() => setIsAddingContact(true)}
              className="bg-lounge-gold hover:bg-lounge-gold/90"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Contact Info
            </Button>
          </div>

          {isAddingContact && (
            <form onSubmit={handleAddContact} className="mb-6 space-y-4 bg-lounge-dark p-4 rounded-lg">
              <Input
                placeholder="Type (e.g., Phone, Email, Address)"
                value={contactForm.type}
                onChange={(e) => setContactForm({ ...contactForm, type: e.target.value })}
                className="bg-lounge-charcoal border-lounge-gold/20"
                required
              />
              <Input
                placeholder="Value"
                value={contactForm.value}
                onChange={(e) => setContactForm({ ...contactForm, value: e.target.value })}
                className="bg-lounge-charcoal border-lounge-gold/20"
                required
              />
              <Input
                placeholder="Label"
                value={contactForm.label}
                onChange={(e) => setContactForm({ ...contactForm, label: e.target.value })}
                className="bg-lounge-charcoal border-lounge-gold/20"
              />
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={contactForm.is_primary}
                  onChange={(e) => setContactForm({ ...contactForm, is_primary: e.target.checked })}
                  className="accent-lounge-gold w-4 h-4 rounded border-lounge-gold/20"
                />
                Is Primary
              </label>
              <Input
                placeholder="Display Order"
                type="number"
                value={contactForm.display_order}
                onChange={(e) => setContactForm({ ...contactForm, display_order: Number(e.target.value) })}
                className="bg-lounge-charcoal border-lounge-gold/20"
              />
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={contactForm.is_public}
                  onChange={(e) => setContactForm({ ...contactForm, is_public: e.target.checked })}
                  className="accent-lounge-gold w-4 h-4 rounded border-lounge-gold/20"
                />
                Is Public
              </label>
              <div className="flex gap-2">
                <Button type="submit" className="bg-lounge-gold hover:bg-lounge-gold/90">
                  Save
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsAddingContact(false);
                    setContactForm({ type: "", label: "", value: "", is_primary: false, display_order: 0, is_public: false });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {contactInfo.map((contact) => (
              <div
                key={contact.id}
                className="flex items-center justify-between p-4 bg-lounge-dark rounded-lg"
              >
                {editingContact === contact.id ? (
                  <div className="flex-1 space-y-4">
                    <Input
                      value={contactForm.type}
                      onChange={(e) => setContactForm({ ...contactForm, type: e.target.value })}
                      className="bg-lounge-charcoal border-lounge-gold/20"
                    />
                    <Input
                      value={contactForm.value}
                      onChange={(e) => setContactForm({ ...contactForm, value: e.target.value })}
                      className="bg-lounge-charcoal border-lounge-gold/20"
                    />
                    <Input
                      placeholder="Label"
                      value={contactForm.label}
                      onChange={(e) => setContactForm({ ...contactForm, label: e.target.value })}
                      className="bg-lounge-charcoal border-lounge-gold/20"
                    />
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={contactForm.is_primary}
                        onChange={(e) => setContactForm({ ...contactForm, is_primary: e.target.checked })}
                        className="accent-lounge-gold w-4 h-4 rounded border-lounge-gold/20"
                      />
                      Is Primary
                    </label>
                    <Input
                      placeholder="Display Order"
                      type="number"
                      value={contactForm.display_order}
                      onChange={(e) => setContactForm({ ...contactForm, display_order: Number(e.target.value) })}
                      className="bg-lounge-charcoal border-lounge-gold/20"
                    />
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={contactForm.is_public}
                        onChange={(e) => setContactForm({ ...contactForm, is_public: e.target.checked })}
                        className="accent-lounge-gold w-4 h-4 rounded border-lounge-gold/20"
                      />
                      Is Public
                    </label>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleUpdateContact(contact.id)}
                        className="bg-lounge-gold hover:bg-lounge-gold/90"
                      >
                        Save
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditingContact(null);
                          setContactForm({ type: "", label: "", value: "", is_primary: false, display_order: 0, is_public: false });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <h3 className="font-medium text-lounge-light">{contact.type}</h3>
                      <p className="text-sm text-lounge-light/60">{contact.value}</p>
                      {contact.link && (
                        <a
                          href={contact.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-lounge-gold hover:underline mt-1 block"
                        >
                          {contact.link}
                        </a>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          setEditingContact(contact.id);
                          setContactForm({
                            type: contact.type,
                            label: contact.label || "",
                            value: contact.value || "",
                            is_primary: contact.is_primary || false,
                            display_order: contact.display_order || 0,
                            is_public: contact.is_public || false,
                          });
                        }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="text-red-500 hover:text-red-600"
                        onClick={() => handleDeleteContact(contact.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminContact; 